<template>
    <div class="relative" ref="rootRef">
        <button
            type="button"
            @click="open = !open"
            class="inline-flex items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50/80 px-3.5 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:ring-offset-1"
            :aria-expanded="open"
            aria-haspopup="listbox"
            aria-controls="header-lang-menu"
        >
            <img
                v-if="currentFlagSrc"
                :src="currentFlagSrc"
                :alt="currentLangLabel"
                class="h-5 w-5 rounded-full object-cover ring-1 ring-gray-200/80 flex-shrink-0"
                width="20"
                height="20"
            />
            <span class="min-w-[2rem] text-left">{{ currentLangLabel }}</span>
            <svg
                class="h-4 w-4 text-gray-500 transition-transform duration-200"
                :class="{ 'rotate-180': open }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
            >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
        </button>
        <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
        >
            <div
                v-show="open"
                id="header-lang-menu"
                role="listbox"
                class="absolute right-0 z-50 mt-2 w-full origin-top-right rounded-lg bg-white p-2 shadow-lg ring-1 ring-black/5 min-w-[28rem]"
            >
                <div class="grid grid-cols-2 gap-x-1 gap-y-0.5">
                    <a
                        v-for="lang in visibleLanguages"
                        :key="lang.code"
                        :href="lang.path || '#'"
                        class="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 rounded-lg"
                        :class="currentLang?.code === lang.code ? 'bg-brand-blue/5 text-brand-blue font-semibold' : ''"
                    >
                        <img
                            v-if="lang.flag"
                            :src="resolvedFlagSrc(lang.flag, lang.code)"
                            :alt="lang.name"
                            class="h-5 w-5 rounded-full object-cover ring-1 ring-gray-200/80 flex-shrink-0"
                            width="20"
                            height="20"
                        />
                        <span
                            v-else
                            class="h-5 w-5 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0"
                        >
                            {{ (lang.code || '').substring(0, 2).toUpperCase() }}
                        </span>
                        <span class="truncate">{{ lang.name }}</span>
                    </a>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { normalizeFlagEntity, resolveFlagUrl } from '../utils/flagUrl.js';

export default {
    name: 'HeaderLanguageSelector',
    props: {
        currentLang: { type: Object, default: null },
        languages: { type: Array, default: () => [] },
        pathsByLanguage: { type: Object, default: () => ({}) },
    },
    setup(props) {
        const open = ref(false);
        const rootRef = ref(null);
        /** useStore() may throw on embed pages without Vuex — fall back to SSR props / __APP_DATA__. */
        let store = null;
        try { store = useStore(); } catch { store = null; }

        const resolvedCurrentLang = computed(() => {
            if (props.currentLang) return normalizeFlagEntity(props.currentLang);
            return normalizeFlagEntity(store?.getters?.lang
                ?? (typeof window !== 'undefined' ? window.__APP_DATA__?.lang : null)
                ?? null);
        });

        const resolvedPathsByLanguage = computed(() => {
            const fromProps = props.pathsByLanguage || {};
            if (Object.keys(fromProps).length) return fromProps;

            const fromStore = store?.state?.page?.paths_by_language;
            if (fromStore && Object.keys(fromStore).length) return fromStore;

            const fromWindow = typeof window !== 'undefined'
                ? window.__APP_DATA__?.page?.paths_by_language
                : null;
            return fromWindow && Object.keys(fromWindow).length ? fromWindow : {};
        });

        const currentLangLabel = computed(() => {
            const c = resolvedCurrentLang.value;
            if (!c) return 'EN';
            if (c.code_view) return String(c.code_view);
            if (c.code) return String(c.code).substring(0, 2).toUpperCase();
            return 'EN';
        });

        function resolvedFlagSrc(flag, code = '') {
            return resolveFlagUrl(flag, code);
        }

        const currentFlagSrc = computed(() => resolvedFlagSrc(
            resolvedCurrentLang.value?.flag,
            resolvedCurrentLang.value?.code
        ));

        /**
         * Prefer the SSR-seeded list from Vuex (filled via `bootstrapFromWindow`
         * → window.__APP_DATA__.languages). Falls back to the prop only when the
         * store is missing or empty, so we never trigger a duplicate /languages
         * fetch on initial paint.
         */
        const resolvedLanguages = computed(() => {
            const fromStore = store?.getters?.languages;
            const source = Array.isArray(fromStore) && fromStore.length
                ? fromStore
                : (props.languages || []);
            return source.map((language) => normalizeFlagEntity(language));
        });

        const visibleLanguages = computed(() => {
            const paths = resolvedPathsByLanguage.value;
            const langs = resolvedLanguages.value;

            const langMap = Object.fromEntries(
                langs.map((l) => [l.code, l])
            );
            const langByUpper = Object.fromEntries(
                langs
                    .filter((l) => l && l.code != null && l.code !== '')
                    .map((l) => [String(l.code).toUpperCase(), l])
            );

            return Object.entries(paths)
                .map(([code, path]) => {
                    const lang =
                        langMap[code]
                        ?? langByUpper[String(code).toUpperCase()];

                    return {
                        ...(lang || {}),
                        code,
                        path,
                        name:
                            lang?.translated?.name
                            ?? lang?.code_view
                            ?? code
                    };
                })
                .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
        });

        function onOutsideClick(e) {
            if (rootRef.value && !rootRef.value.contains(e.target)) {
                open.value = false;
            }
        }

        onMounted(() => {
            document.addEventListener('click', onOutsideClick);
            /**
             * If SSR bootstrap delivered languages (the normal case) the store
             * already has them and `loadLanguages` resolves synchronously without
             * hitting the network. The action only fetches as a fallback when the
             * store is empty (e.g. embed pages without window.__APP_DATA__).
             */
            if (store) {
                store.dispatch('loadLanguages').catch(() => {});
            }
        });

        onBeforeUnmount(() => {
            document.removeEventListener('click', onOutsideClick);
        });

        return {
            open,
            rootRef,
            visibleLanguages,
            currentLangLabel,
            currentFlagSrc,
            resolvedFlagSrc,
            currentLang: resolvedCurrentLang,
        };
    },
};

</script>
